import React from 'react'
import { useIntl } from 'react-intl'
import * as SC from './RadioDateSlotsFormFieldStyles'
import { RadioButtonOption, RadioButtonsFormFieldProps, RadioDateSlotOptionGroup } from './types'
import FormItem from '../../atoms/FormItem/FormItem'
import VisuallyHidden from '../../atoms/VisuallyHidden/VisuallyHidden'
import useMessages from '../../hooks/useMessages'

const hasGroupLabel = (label: RadioDateSlotOptionGroup['label']): label is React.ReactNode => {
	return label != null && label !== false && label !== ''
}

const RadioDateSlotsFormField = <V extends string | number>(props: RadioButtonsFormFieldProps<V>) => {
	const intl = useIntl()
	const { messages } = useMessages()
	const {
		options,
		optionGroups,
		disabled,
		input: { value, onChange, name },
		meta: { invalid, error },
		onAfterChange,
		optionRender,
		inputStyle = 'bordered',
		htmlFor,
		label,
		labelScreenReaderOnly,
		required
	} = props

	const invalidStatus = !!(error || invalid)

	const groups: RadioDateSlotOptionGroup<V>[] =
		optionGroups ??
		(options?.length
			? [
					{
						id: 'default',
						options
					}
				]
			: [])

	const handleChangeValue = (e: React.ChangeEvent<HTMLInputElement>, option: V) => {
		if (e.target.checked) {
			onChange(option)
			onAfterChange?.(option)
		}
	}

	const renderOption = (option: RadioButtonOption<V>, groupLabel?: RadioDateSlotOptionGroup['label']) => {
		const checked = option.id === value
		const isOptionDisabled = !!(disabled || option.disabled)
		const useAccessibleLabel = !!option.accessibleLabel

		const optionNode = (
			<SC.RadioButton
				$checked={checked}
				$disabled={isOptionDisabled}
				key={String(option.id)}
				$inputStyle={inputStyle}
				checked={checked}
				name={name}
				disabled={isOptionDisabled}
				onChange={(e) => handleChangeValue(e, option.id)}
			>
				<SC.SlotContent>
					<SC.SlotHeader>
						<SC.SlotTime $checked={checked} $disabled={isOptionDisabled}>
							{useAccessibleLabel ? (
								<>
									<VisuallyHidden>{option.accessibleLabel}</VisuallyHidden>
									<span aria-hidden>{option.label}</span>
								</>
							) : (
								<>
									{hasGroupLabel(groupLabel) ? <VisuallyHidden>{groupLabel}, </VisuallyHidden> : null}
									{option.label}
								</>
							)}
						</SC.SlotTime>
					</SC.SlotHeader>
					<SC.SlotFooter>
						<SC.SpotsLeft $disabled={isOptionDisabled}>{intl.formatMessage({ id: 'Spots left' }, { count: option.spotsLeft })}</SC.SpotsLeft>
						<SC.Capacity $disabled={isOptionDisabled}>
							<SC.PeopleIcon $disabled={isOptionDisabled} aria-hidden />
							<VisuallyHidden>{intl.formatMessage({ id: 'Capacity' }, { count: option.capacity })}</VisuallyHidden>
							<div aria-hidden>{option.capacity}</div>
						</SC.Capacity>
					</SC.SlotFooter>
					{option.alreadyBooked && (
						<SC.AlreadyBookedWrapper>
							<SC.GreenCheckIcon $disabled={isOptionDisabled} aria-hidden />
							<SC.AlreadyBookedText $disabled={isOptionDisabled}>{messages['You already booked a slot for this event']}</SC.AlreadyBookedText>
						</SC.AlreadyBookedWrapper>
					)}
				</SC.SlotContent>
			</SC.RadioButton>
		)

		return optionRender ? (
			<React.Fragment key={String(option.id)}>{optionRender(optionNode, { option, checked, disabled: isOptionDisabled })}</React.Fragment>
		) : (
			optionNode
		)
	}

	return (
		<FormItem
			asFieldset={!!label}
			label={label}
			labelScreenReaderOnly={labelScreenReaderOnly}
			required={required}
			htmlFor={htmlFor}
			error={invalidStatus && error ? error : undefined}
		>
			<SC.RadioButtonsWrapper>
				{groups.map((group) => {
					const optionsNode = group.options.map((option) => renderOption(option, group.label))

					if (!hasGroupLabel(group.label)) {
						return <SC.OptionGroupOptions key={group.id}>{optionsNode}</SC.OptionGroupOptions>
					}

					return (
						<SC.OptionGroup key={group.id}>
							{/* Date is also in each radio name for AT; hide the visual heading from the accessibility tree to avoid double announcement. */}
							<SC.OptionGroupLabel aria-hidden>{group.label}</SC.OptionGroupLabel>
							<SC.OptionGroupOptions>{optionsNode}</SC.OptionGroupOptions>
						</SC.OptionGroup>
					)
				})}
			</SC.RadioButtonsWrapper>
		</FormItem>
	)
}

export default RadioDateSlotsFormField
